import { ComponentProps, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import {
  useGetGlobalFeaturedAdventuresQuery,
  useGetMarketAdventuresQuery,
} from 'hooks/useQueries'

import SuggestedAdventures from '../../../adventure/SuggestedAdventures'
import Container from '../../../../components/layout/Container'
import SimpleLink from '../../../../components/display/SimpleLink'

import { CallToActionButton, Title, Subtitle } from '../../shared'
import { defaultStateCodeForMarket } from '../../utils'

const SET_SIZE = 4

type FeaturedAdventuresProps = {
  adventures: ComponentProps<typeof SuggestedAdventures>['adventures']
  ctaSearch: string
  handleFetchMore: () => any
  hasNextPage: boolean
  loading: boolean
  notYetFetched: boolean
}

const FeaturedAdventures = ({
  adventures,
  ctaSearch,
  handleFetchMore,
  hasNextPage,
  loading,
  notYetFetched,
}: FeaturedAdventuresProps) => {
  const [t] = useTranslation('spelling')

  const noAdventures = !adventures || adventures?.edges?.length === 0

  const isEmpty = !loading && noAdventures

  return (
    <Container>
      <SuggestedAdventures
        adventures={adventures}
        inItineraryContext={false}
        canFetchMore={hasNextPage}
        loading={notYetFetched}
        isEmpty={isEmpty}
        handleFetchMore={handleFetchMore}
        maxSlidesDesktop={SET_SIZE}
        customTitle={
          <div>
            <Title>Discover local adventures</Title>
            <Subtitle>
              Get authentic adventure tips from {t('traveler')}s on the ground
            </Subtitle>
          </div>
        }
      />

      {!isEmpty && (
        <SimpleLink
          to={{
            pathname: '/listings',
            search: ctaSearch,
            state: {
              scrollTop: true,
            },
          }}
        >
          <CallToActionButton as="div" rounded>
            Discover More Adventures
          </CallToActionButton>
        </SimpleLink>
      )}
    </Container>
  )
}

type MarketFeaturedAdventuresProps = {
  marketCode: GQL_MarketCode
  countryCode: GQL_Countries
}

export const MarketFeaturedAdventures = ({
  marketCode,
  countryCode = 'AU',
}: MarketFeaturedAdventuresProps) => {
  const { loading, data, fetchMore } = useGetMarketAdventuresQuery({
    variables: {
      market: marketCode,
      first: SET_SIZE,
    },
  })

  const adventures = data?.market?.adventures
  const pageInfo = data?.market?.adventures?.pageInfo

  const endCursor = pageInfo?.endCursor
  const hasNextPage = pageInfo?.hasNextPage

  const handleFetchMore = useCallback(() => {
    fetchMore({
      variables: {
        first: SET_SIZE,
        cursor: endCursor,
      },
    })
  }, [fetchMore, pageInfo])

  const notYetFetched = loading || !data

  const stateParam = defaultStateCodeForMarket(marketCode)

  const ctaSearch = `?country=${countryCode}&type=adventures&state=${stateParam}`

  return (
    <FeaturedAdventures
      adventures={adventures}
      ctaSearch={ctaSearch}
      handleFetchMore={handleFetchMore}
      hasNextPage={hasNextPage}
      loading={loading}
      notYetFetched={notYetFetched}
    />
  )
}

type GlobalFeaturedAdventuresProps = {
  countryCode?: GQL_Countries
}

export const GlobalFeaturedAdventures = ({
  countryCode,
}: GlobalFeaturedAdventuresProps) => {
  const { loading, data, fetchMore } = useGetGlobalFeaturedAdventuresQuery({
    variables: {
      country: countryCode,
      first: SET_SIZE,
    },
  })

  const adventures = data?.featuredAdventures
  const pageInfo = data?.featuredAdventures?.pageInfo

  const endCursor = pageInfo?.endCursor
  const hasNextPage = pageInfo?.hasNextPage

  const handleFetchMore = useCallback(() => {
    fetchMore({
      variables: {
        first: SET_SIZE,
        cursor: endCursor,
      },
    })
  }, [fetchMore, pageInfo])

  const notYetFetched = loading || !data

  const ctaSearch = `?country=AU&type=adventures&state=all`

  return (
    <FeaturedAdventures
      adventures={adventures}
      ctaSearch={ctaSearch}
      handleFetchMore={handleFetchMore}
      hasNextPage={hasNextPage}
      loading={loading}
      notYetFetched={notYetFetched}
    />
  )
}

export default FeaturedAdventures
